import { api, ApiError } from '../../../core/network/api';
import { renderAsync } from '../async-view';
import { formatNumber, t } from '../../i18n';
import { refreshPlayerProgress } from '../player-progress-store';
import { setNavBadge } from '../nav-badges-store';
import {
  badge,
  type BadgeTone,
  button,
  card,
  emptyState,
  icon,
  type IconName,
  progressBar,
  sectionHeading,
  tabStrip,
} from '../ui';
import { countUp, rewardBurst } from '../fx';
import { playSample } from '../menu-sfx';
import type { RouteMount } from './types';

/**
 * The Quests route.
 *
 * Reads through the **game-side** `GET /api/game/quests` (never core's raw `GET /api/quests`)
 * because assignment underneath the raw endpoint is destructive — see `game-quest.service.ts`.
 * Claim goes through core's `POST /api/quests/:id/claim`, which is a plain read-then-flip and
 * safe to call directly.
 */
type QuestType = 'DAILY' | 'WEEKLY';
type QuestStatus = 'ACTIVE' | 'COMPLETED' | 'CLAIMED';

interface QuestDefinitionDto {
  name: string;
  description: string;
  questType: QuestType;
  /**
   * What the quest counts — `kills`, `match_played`, and so on.
   *
   * Already in the payload (`getActiveQuests` includes the whole quest row); it was simply
   * never declared here. Read only to pick the medal: see `questArt`.
   */
  action: string;
  targetCount: number;
  rewardSoft: number;
  rewardPremium: number;
  rewardXp: number;
}

interface UserQuestDto {
  id: string;
  progress: number;
  status: QuestStatus;
  quest: QuestDefinitionDto;
}

/** Catalogue keys, resolved at render time — a module-level `t()` would freeze at boot. */
const STATUS_LABEL_KEY: Record<QuestStatus, string> = {
  ACTIVE: 'quests.inProgress',
  COMPLETED: 'quests.readyToClaim',
  CLAIMED: 'common.claimed',
};

const STATUS_TONE: Record<QuestStatus, BadgeTone> = {
  ACTIVE: 'neutral',
  COMPLETED: 'gold',
  CLAIMED: 'success',
};

/** One read of the OS setting per call — a person can change it mid-session, and `fx.ts`'s own
 * copy of this check is not exported for a route file to reuse. */
function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The icon a quest wears, chosen from what it asks you to DO.
 *
 * Off `quest.action`, which is the field the tracker already keys on — not off the quest's
 * name, which is content and will be rewritten by whoever writes the next batch of quests.
 * An action with no icon of its own falls back to the star rather than rendering nothing.
 */
function questIcon(action: string): IconName {
  if (action.includes('headshot')) return 'headshot';
  if (action.includes('kill')) return 'kills';
  if (action.includes('win')) return 'trophy';
  if (action.includes('bomb') || action.includes('plant') || action.includes('defuse')) return 'bomb';
  if (action.includes('streak')) return 'streak';
  if (action.includes('time') || action.includes('played')) return 'time';
  return 'star';
}

/** The `done` stamp laid across a claimed quest's corner. */
function doneStamp(): HTMLElement {
  const stamp = icon('done', { size: 44, className: 'kit-pop absolute -top-3 -right-2 z-10 rotate-12 [filter:drop-shadow(0_3px_3px_rgba(0,0,0,0.7))]' });
  stamp.dataset.quests = 'stamp';
  return stamp;
}

/** A value in a painted slot, its icon over the left end; returns the figure's node for counting. */
function figureChip(art: IconName, title: string, initial: string): { el: HTMLElement; value: HTMLElement } {
  const el = document.createElement('span');
  el.className = 'kit-chip [--kh:34px]';
  el.title = title;
  el.setAttribute('aria-label', `${title}: ${initial}`);
  const value = document.createElement('span');
  value.className = 'kit-label font-heading text-[14px] tabular-nums';
  value.textContent = initial;
  el.append(icon(art, { size: 24, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }), value);
  return { el, value };
}

/**
 * Primes a just-mounted progress bar to animate in from empty, motion permitting.
 *
 * `progressBar` already draws the correct final width — this only adds the "started from
 * nothing" arrival on top, for a player who CAN see it. The width is captured and restored
 * rather than recomputed, so it can never drift from what the bar itself decided to draw. Two
 * rAFs, not one: a single frame can land before the browser has committed the 0% paint, and the
 * flip to the real width then never has a first frame to transition away from.
 */
function primeBarForEntrance(bar: HTMLElement): void {
  if (prefersReducedMotion()) return;
  const fill = bar.firstElementChild as HTMLElement | null;
  if (!fill) return;
  const target = fill.style.width;
  fill.style.width = '0%';
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      fill.style.width = target;
    });
  });
}

function questCard(uq: UserQuestDto, index: number, onClaimed: () => void): HTMLElement {
  const body = document.createElement('div');
  body.className = 'flex-1 min-w-0 flex flex-col gap-2';

  const header = document.createElement('div');
  header.className = 'flex flex-wrap items-center justify-between gap-x-2 gap-y-1';
  const name = document.createElement('span');
  name.className = 'kit-label font-heading uppercase text-[15px] min-w-0 leading-tight [overflow-wrap:anywhere]';
  name.textContent = uq.quest.name;
  let statusBadge = badge(t(STATUS_LABEL_KEY[uq.status]), STATUS_TONE[uq.status]);
  header.append(name, statusBadge);

  const description = document.createElement('p');
  description.className = 'text-[12px] text-text-dim leading-snug';
  description.textContent = uq.quest.description;

  const bar = progressBar({
    value: uq.progress,
    max: uq.quest.targetCount,
    label: t('quests.progressAria', { name: uq.quest.name }),
    accent: 'cyan',
  });
  primeBarForEntrance(bar);
  bar.classList.replace('w-full', 'flex-1');

  // The count rides in its own slot beside the bar, not on a paragraph underneath it.
  const progressChip = document.createElement('span');
  progressChip.className = 'kit-chip [--kh:30px] kit-label font-heading text-[13px] tabular-nums';
  progressChip.textContent = `${Math.min(uq.progress, uq.quest.targetCount)} / ${uq.quest.targetCount}`;

  const progressRow = document.createElement('div');
  progressRow.className = 'flex items-center gap-2';
  progressRow.append(bar, progressChip);

  // The reward is what the player is here for, so it is drawn: the coin and the XP arrow in
  // their own slots, not a sentence beginning "Reward:".
  const rewardLine = document.createElement('div');
  rewardLine.className = 'flex flex-wrap items-center gap-2';
  rewardLine.append(
    figureChip('credits', t('quests.creditsOnOffer'), formatNumber(uq.quest.rewardSoft)).el,
    figureChip('xp', 'XP', formatNumber(uq.quest.rewardXp)).el,
  );

  const errorText = document.createElement('p');
  errorText.className = 'text-[12px] text-error hidden';

  const footer = document.createElement('div');
  footer.className = 'flex flex-wrap items-center justify-between gap-2';
  footer.append(rewardLine);

  // Assigned once the card is built, below — declared here so the click handler's closure can
  // reach it.
  let cardEl: HTMLElement;

  // A Claim button that cannot be pressed is noise: the status chip and the bar already say
  // "not yet". It appears only once there is something to claim.
  if (uq.status === 'COMPLETED') {
    const claimBtn = button(t('common.claim'), {
      variant: 'primary',
      className: 'min-w-[96px]',
      onClick: () => {
        claimBtn.disabled = true;
        claimBtn.textContent = t('common.claiming');
        errorText.classList.add('hidden');
        api
          .post(`/quests/${uq.id}/claim`)
          .then(() => {
            // Fired BEFORE the button is removed: the burst is anchored to that element's box,
            // and a detached node has no box to anchor to.
            playSample('collect');
            rewardBurst(claimBtn, { label: `+${uq.quest.rewardSoft}`, tone: 'gold' });
            const updated = badge(t(STATUS_LABEL_KEY.CLAIMED), STATUS_TONE.CLAIMED);
            statusBadge.replaceWith(updated);
            statusBadge = updated;
            claimBtn.remove();
            // The card settles: the teal "ready" glow comes off — there is no longer an action
            // to ask for — and the `done` stamp lands on it.
            cardEl.classList.remove('kit-selected');
            cardEl.append(doneStamp());
            if (!prefersReducedMotion() && typeof rewardLine.animate === 'function') {
              rewardLine.animate(
                [{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
                { duration: 420, easing: 'ease-out' },
              );
            }
            uq.status = 'CLAIMED';
            onClaimed();
            void refreshPlayerProgress();
          })
          .catch((error: unknown) => {
            claimBtn.disabled = false;
            claimBtn.textContent = t('common.claim');
            errorText.textContent =
              error instanceof ApiError ? error.message : t('quests.claimFailed');
            errorText.classList.remove('hidden');
          });
      },
    });
    claimBtn.dataset.quests = 'claim';
    footer.append(claimBtn);
  }

  body.append(header, description, progressRow, footer, errorText);

  // The big icon sits in its own recess, lit only once the quest is finished — an unearned
  // quest that already glows is a reward handed out for showing up.
  const iconWell = document.createElement('span');
  iconWell.className = 'kit-well shrink-0 grid place-items-center w-14 h-14 sm:w-[72px] sm:h-[72px]';
  const glyph = icon(questIcon(uq.quest.action), {
    size: 64,
    className: 'w-10 h-10 sm:w-14 sm:h-14 [filter:drop-shadow(0_3px_3px_rgba(0,0,0,0.7))]' + (uq.status === 'ACTIVE' ? ' brightness-75 saturate-75' : ''),
  });
  iconWell.append(glyph);

  cardEl = document.createElement('div');
  cardEl.className = [
    'kit-plate kit-deal flex items-start gap-3 sm:gap-4 min-w-0',
    uq.status === 'COMPLETED' ? 'kit-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');
  cardEl.style.setProperty('--i', String(index));
  cardEl.append(iconWell, body);
  if (uq.status === 'CLAIMED') cardEl.append(doneStamp());
  return cardEl;
}

/**
 * Hours until the next UTC reset boundary.
 *
 * Daily quests reset at 00:00 UTC and weekly on Monday 00:00 UTC (`core/cron`), which is a
 * fact of the schedule rather than a value any endpoint returns — so it is computed here from
 * the same two constants the cron is configured with. Rounded up: "resets in 0h" on a board
 * that still has fifty minutes on it is worse than no countdown at all.
 */
function hoursUntilReset(now: Date, weekly: boolean): number {
  const next = new Date(now);
  next.setUTCHours(0, 0, 0, 0);
  if (weekly) {
    // Monday is 1 in `getUTCDay`; Sunday is 0 and is six days from the next Monday.
    const daysAhead = (8 - now.getUTCDay()) % 7 || 7;
    next.setUTCDate(next.getUTCDate() + daysAhead);
  } else {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return Math.max(1, Math.ceil((next.getTime() - now.getTime()) / 3_600_000));
}

/** The reset countdown for one board, as a clock icon in a slot. */
function resetChip(weekly: boolean): HTMLElement {
  const hours = hoursUntilReset(new Date(), weekly);
  const chip = figureChip('time', t(weekly ? 'quests.weeklyIn' : 'quests.dailyReset', { hours }), `${hours}h`).el;
  chip.dataset.quests = 'reset';
  return chip;
}

interface SummaryHandle {
  el: HTMLElement;
  /** Recomputes every figure from the (mutated in place) quest list and counts each to its new
   * value — never a re-render, so the rest of the page — and the reader's scroll position —
   * never moves. */
  update(quests: UserQuestDto[]): void;
}

/**
 * What the whole board is worth: how many are ready, and the credits and XP still on offer.
 * Every figure is summed from the rows already on screen; nothing new is fetched.
 */
function summaryStrip(quests: UserQuestDto[]): SummaryHandle {
  const intFormat = (v: number) => String(Math.round(v));
  const softFormat = (v: number) => formatNumber(Math.round(v));

  const compute = (list: UserQuestDto[]) => {
    const open = list.filter((q) => q.status !== 'CLAIMED');
    return {
      ready: list.filter((q) => q.status === 'COMPLETED').length,
      soft: open.reduce((total, q) => total + q.quest.rewardSoft, 0),
      xp: open.reduce((total, q) => total + q.quest.rewardXp, 0),
    };
  };

  let current = compute(quests);
  const ready = figureChip('done', t('quests.readyToClaim'), '0');
  const credits = figureChip('credits', t('quests.creditsOnOffer'), '0');
  const xp = figureChip('xp', 'XP', '0');

  const strip = document.createElement('div');
  strip.dataset.quests = 'summary';
  strip.className = 'flex flex-wrap items-center gap-2';
  strip.append(ready.el, credits.el, xp.el);

  countUp(ready.value, 0, current.ready, { format: intFormat });
  countUp(credits.value, 0, current.soft, { format: softFormat });
  countUp(xp.value, 0, current.xp, { format: softFormat });

  function update(nextQuests: UserQuestDto[]): void {
    const next = compute(nextQuests);
    countUp(ready.value, current.ready, next.ready, { format: intFormat });
    countUp(credits.value, current.soft, next.soft, { format: softFormat });
    countUp(xp.value, current.xp, next.xp, { format: softFormat });
    current = next;
  }

  return { el: strip, update };
}

/** The two boards, in tab order; a board with no quests is not offered. */
const BOARDS: { type: QuestType; labelKey: string; art: IconName }[] = [
  { type: 'DAILY', labelKey: 'quests.daily', art: 'time' },
  { type: 'WEEKLY', labelKey: 'quests.weekly', art: 'trophy' },
];

export const mountQuestsRoute: RouteMount = (outlet) => {
  const heading = sectionHeading(t('quests.title'));

  const content = document.createElement('div');
  outlet.append(heading, content);

  const dispose = renderAsync<UserQuestDto[]>(content, {
    load: () => api.get<UserQuestDto[]>('/game/quests'),
    isEmpty: (quests) => quests.length === 0,
    renderEmpty(container) {
      container.innerHTML = '';
      container.append(
        card([
          emptyState({
            art: 'quests',
            title: t('quests.emptyTitle'),
            hint: t('quests.emptyBody'),
          }),
        ]),
      );
    },
    renderContent(quests, container) {
      container.innerHTML = '';
      /**
       * The nav dot, from this screen's own rows.
       *
       * It used to be published from one place only — `play-panels.ts`, when the Play screen
       * fetched the board — so claiming every quest here left the red dot on the Quests tab
       * until the page was reloaded and Play fetched again (QA item 25). This is the screen
       * that knows first, and `quests` is the same array the claim handlers mutate, so the
       * same call answers both "what did the server just say" and "what is left now".
       */
      const publishBadge = () => setNavBadge('quests', quests.some((q) => q.status === 'COMPLETED'));
      publishBadge();

      const boards = BOARDS.filter((board) => quests.some((q) => q.quest.questType === board.type));
      const readyOn = (type: QuestType) =>
        quests.filter((q) => q.quest.questType === type && q.status === 'COMPLETED').length;
      // Open on the board that has something to collect, else the first one that exists.
      let active = (boards.find((board) => readyOn(board.type) > 0) ?? boards[0]).type;

      const summary = summaryStrip(quests);
      const list = document.createElement('div');
      list.dataset.quests = 'list';
      // Two up from `lg`: a quest plate holds a name, a sentence, a bar and a reward row.
      list.className = 'grid gap-3 lg:grid-cols-2';

      const strip = tabStrip(
        boards.map((board) => ({ id: board.type, label: t(board.labelKey), art: board.art })),
        active,
        (id) => {
          active = id as QuestType;
          strip.setActive(id);
          renderBoard();
        },
      );
      const tabButtons = Array.from(strip.el.querySelectorAll<HTMLElement>('[role="tab"]'));
      // A red count on a tab whose board has a reward waiting — the other board is not on screen.
      const paintTabBadges = () => {
        tabButtons.forEach((tab, i) => {
          tab.querySelector('[data-quests="tab-badge"]')?.remove();
          const ready = readyOn(boards[i].type);
          if (ready === 0) return;
          const dot = document.createElement('span');
          dot.className = 'kit-badge';
          dot.dataset.quests = 'tab-badge';
          dot.textContent = String(ready);
          tab.append(dot);
        });
      };

      const timer = document.createElement('div');
      const toolbar = document.createElement('div');
      toolbar.className = 'flex flex-wrap items-end justify-between gap-3';
      toolbar.append(strip.el, timer);

      function renderBoard(): void {
        timer.replaceChildren(resetChip(active === 'WEEKLY'));
        list.replaceChildren(
          ...quests
            .filter((q) => q.quest.questType === active)
            .map((q, i) => questCard(q, i, onClaimed)),
        );
      }
      // A claim anywhere on the board updates the same figures from the same (mutated) `quests`
      // array, in place.
      function onClaimed(): void {
        summary.update(quests);
        paintTabBadges();
        publishBadge();
      }

      renderBoard();
      paintTabBadges();
      const board = card([summary.el, toolbar, list]);
      container.append(board);
    },
  });

  return () => {
    dispose();
    heading.remove();
    content.remove();
  };
};
